import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, AlertCircle } from "lucide-react";
import { handleGet, handleSend } from "../../../controllers/controller.all";
import SubmitButton from "../../../components/ui/SubmitButton";


export default function AbsenceCreate() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [classes, setClasses] = useState([]);
    const [eleves, setEleves] = useState([]);
    const [matieres, setMatieres] = useState([]);

    const [hasAnnee, setHasAnnee] = useState(true);
    const [checking, setChecking] = useState(true);

    const [formData, setFormData] = useState({
        classe: "",
        eleve: "",
        matiere: "",
        remarques: "",
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            // Check Annee Scolaire
            await handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
                if (!res?.annees || res.annees.length === 0) setHasAnnee(false);
            }, token);
            setChecking(false);

            // Fetch Classes
            await handleGet(`/classe/ecole/${ecoleId}`, (res) => {
                if (res?.classes) setClasses(res.classes);
            }, token);

            // Fetch Matieres
            await handleGet(`/matiere/ecole/${ecoleId}`, (res) => {
                if (res?.matieres) setMatieres(res.matieres);
            }, token);
        };

        if (ecoleId && token) fetchInitialData();
    }, [ecoleId, token]);

    // When a class is selected, fetch students of that class
    useEffect(() => {
        if (!formData.classe) {
            setEleves([]);
            setFormData(prev => ({ ...prev, eleve: "" }));
            return;
        }

        const fetchEleves = async () => {
            await handleGet(`/user/classe/${formData.classe}`, (res) => {
                if (res?.users) setEleves(res.users);
            }, token);
        };
        fetchEleves();
    }, [formData.classe, token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // AbsenceModel on backend requires: eleve, matiere, remarques, ecole, creator
        const payload = {
            eleve: formData.eleve,
            matiere: formData.matiere,
            remarques: formData.remarques,
            ecole: ecoleId,
            creator: user?._id || user?.id,
            status: "EN COURS"
        };

        const res = await handleSend(e, "/absence", payload, setLoading, token);
        if (res) {
            navigate("/absences");
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/absences" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Nouvelle Absence</h1>
                    <p className="text-sm text-gray-500">Déclarez une absence pour un élève.</p>
                </div>
            </div>

            {!checking && !hasAnnee && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold">Aucune année scolaire active !</h3>
                        <p className="text-sm mt-1">Vous devez d'abord créer et activer une année scolaire avant de pouvoir ajouter des absences.</p>
                        <Link to="/annees" className="inline-block mt-3 text-sm font-medium text-orange-800 bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition">
                            Aller à la gestion des années scolaires
                        </Link>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Classe *</label>
                        <select
                            required
                            name="classe"
                            value={formData.classe}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Sélectionner une classe</option>
                            {classes.map(c => (
                                <option key={c._id} value={c._id}>{c.libelle}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Élève *</label>
                        <select
                            required
                            name="eleve"
                            value={formData.eleve}
                            onChange={handleChange}
                            disabled={!formData.classe}
                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                            <option value="">Sélectionner un élève</option>
                            {eleves.map(e => (
                                <option key={e._id} value={e._id}>{e.name || e.prenom + ' ' + e.nom}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Matière *</label>
                        <select
                            required
                            name="matiere"
                            value={formData.matiere}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Sélectionner une matière</option>
                            {matieres.map(m => (
                                <option key={m._id} value={m._id}>{m.libelle}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Remarques / Motif</label>
                        <textarea
                            name="remarques"
                            value={formData.remarques}
                            onChange={handleChange}
                            rows="4"
                            placeholder="Entrez un motif ou une remarque (optionnel)..."
                            className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        ></textarea>
                    </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/absences" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton type="submit" isSubmitting={loading} disabled={loading || !hasAnnee} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="h-4 w-4" />
                        {loading ? "Enregistrement..." : "Enregistrer"}
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
